import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Check, ChevronsUpDown, Plus, PauseCircle } from 'lucide-react';
import { useWorkspace, isUsableMembership, isSuspendedMembership } from './WorkspaceProvider';
import { NewWorkspaceDialog } from './NewWorkspaceDialog';
import { FounderBootstrapDialog } from './FounderBootstrapDialog';
import { useAdvisorFirm } from '../advisor/useAdvisorFirm';
import { ADVISOR_CLIENTS_PATH } from '../advisor/useClientFrame';
import { isOrgAdmin } from '../api/types';
import { UserText } from '../components/UserText';

const ICON_STROKE = 1.75;

/**
 * Workspace switcher — a compact chip at the left of the top app bar (nav-IA §7,
 * relocated 2026-07-04 from the sidebar brand block: scope is app-agnostic shell
 * chrome, and sits left of the breadcrumb — "whose data" leads "where"). Shows the
 * active workspace, lists the others, and offers the create affordances. Only the
 * active workspace's name shows in everyday use; the picker appears on click.
 *
 * Two distinct create doors, on independent authorities: "Add a workspace"
 * (tenant-admin of the active org) and "Create organization" (an unconsumed signup
 * grant). This is the ONLY place a single-workspace user meets the second one, since
 * `resolveActive` takes them straight into their workspace and past /select-workspace.
 */
export function WorkspaceSwitcher({ onNavigate }: { onNavigate?: () => void }) {
  const { memberships, activeWorkspaceId, activeMembership, canBootstrapTenant, switchWorkspace } = useWorkspace();
  const { advisorFor } = useAdvisorFirm();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [showNew, setShowNew] = useState<'bootstrap' | 'workspace' | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  const active = memberships.filter(isUsableMembership);
  const suspended = memberships.filter(isSuspendedMembership);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  // Nothing to show until the user has a workspace.
  if (!activeMembership) return null;

  // "Add a workspace" is an org-admin capability (decisions log §41 / WC2): only a
  // tenant-admin can add a workspace to the org (appcore 403s everyone else). Check
  // across the active tenant's memberships, not just the active one — a founder active
  // in a second workspace holds TENANT_ADMIN on their first membership, not this one.
  const canAddWorkspace = memberships.some(
    m => m.tenantId === activeMembership.tenantId && isOrgAdmin(m.roleIds),
  );

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Workspace: ${activeMembership.workspaceName}. Switch workspace`}
        className="group flex items-center gap-2 max-sm:min-h-11 rounded-token border border-line bg-cream/60 px-2.5 py-1.5 cursor-pointer transition-colors duration-150 hover:border-line-strong"
      >
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-token bg-navy/8 font-display text-[11px] font-semibold text-navy">
          {activeMembership.workspaceName.slice(0, 1).toUpperCase()}
        </span>
        <span className="hidden sm:block min-w-0 max-w-[160px] truncate text-left text-[12px] font-semibold text-navy font-sans">
          <UserText>{activeMembership.workspaceName}</UserText>
        </span>
        <ChevronsUpDown size={14} strokeWidth={ICON_STROKE} aria-hidden className="shrink-0 text-slate" />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute left-0 top-full z-50 mt-1 w-64 rounded-token border border-line bg-surface py-1.5 shadow-[0_10px_30px_-10px_rgba(15,23,42,0.2)]"
        >
          {active.map(m => {
            const isActive = m.workspaceId === activeWorkspaceId;
            return (
              <button
                key={m.workspaceId}
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  if (!isActive) {
                    // switchWorkspace's default is to stay on the current path — right
                    // for one client workspace to another, wrong for the one membership
                    // whose "current path" is a family page that workspace has no data
                    // for at all: the advisor's own firm workspace. Send that one
                    // straight to the advisor portal instead (docs/25, steward-app#700).
                    //
                    // ⚠️ **The second arm below is the mirror image of the first, and is
                    // needed for the SAME reason** (steward-app#1409 code review, filed
                    // the same day as the fix it's a sibling of). Picking any OTHER
                    // membership while standing on an advisor-only page (`/advisor/*`) has
                    // the identical problem in reverse: staying put lands the new
                    // (non-firm) workspace on a page — Overview, Clients, Intake
                    // submissions, Your firm — that only makes sense for the FIRM
                    // workspace. Before this arm existed, `switchWorkspace` computed
                    // `target = path` (unchanged), so `WorkspaceProvider`'s
                    // `switchInFlight` (added by #1409 for the "Open" button's own
                    // `destination='/'` call) never turned true — nothing asked the URL to
                    // move — and `RequireAdvisorFirm`'s self-heal fired on the very next
                    // render and switched straight back to the firm. The pick looked like
                    // a dead menu item: reproduced directly by driving the real
                    // `WorkspaceProvider`/`RequireAdvisorFirm` tree with
                    // `switchWorkspace(clientId, undefined)` from `/advisor/clients`.
                    // Sending it to `/` (same destination `openClient` already uses)
                    // fixes it the same way: `switchInFlight` now correctly reads `true`
                    // for the transitional render, so the self-heal waits for the route to
                    // actually leave `/advisor` before deciding anything.
                    const destination = advisorFor && m.workspaceId === advisorFor.firmWorkspaceId
                      ? ADVISOR_CLIENTS_PATH
                      : location.pathname.startsWith('/advisor') ? '/' : undefined;
                    switchWorkspace(m.workspaceId, destination);
                    onNavigate?.();
                  }
                }}
                className="flex w-full items-center gap-2 px-3 py-2 bg-transparent border-none cursor-pointer text-left transition-colors duration-150 hover:bg-cream"
              >
                <span aria-hidden="true" className="flex h-6 w-6 shrink-0 items-center justify-center rounded-token bg-navy/8 font-display text-[11px] font-semibold text-navy">
                  {m.workspaceName.slice(0, 1).toUpperCase()}
                </span>
                <span data-testid="workspace-name" className="min-w-0 flex-1 truncate text-[12px] font-medium text-navy font-sans"><UserText>{m.workspaceName}</UserText></span>
                {isActive && <Check size={14} strokeWidth={ICON_STROKE} aria-hidden className="shrink-0 text-bronze" />}
              </button>
            );
          })}
          {suspended.length > 0 && (
            <>
              <div className="my-1 h-px bg-navy/6" />
              {suspended.map(m => (
                <button
                  key={m.workspaceId}
                  role="menuitem"
                  onClick={() => { setOpen(false); navigate('/select-workspace'); onNavigate?.(); }}
                  className="flex w-full items-center gap-2 px-3 py-2 bg-transparent border-none cursor-pointer text-left transition-colors duration-150 hover:bg-cream"
                >
                  <span aria-hidden="true" className="flex h-6 w-6 shrink-0 items-center justify-center rounded-token bg-navy/8 font-display text-[11px] font-semibold text-slate">
                    {m.workspaceName.slice(0, 1).toUpperCase()}
                  </span>
                  <span data-testid="workspace-name" className="min-w-0 flex-1 truncate text-[12px] font-medium text-slate font-sans"><UserText>{m.workspaceName}</UserText></span>
                  <PauseCircle size={14} strokeWidth={ICON_STROKE} aria-hidden className="shrink-0 text-slate" />
                </button>
              ))}
            </>
          )}
          {(canAddWorkspace || canBootstrapTenant) && (
            <>
              <div className="my-1 h-px bg-navy/6" />
              {canAddWorkspace && (
                <button
                  role="menuitem"
                  onClick={() => { setOpen(false); setShowNew('workspace'); }}
                  className="flex w-full items-center gap-2 px-3 py-2 bg-transparent border-none cursor-pointer text-left font-sans text-[11px] font-semibold uppercase tracking-[0.06em] text-slate transition-colors duration-150 hover:text-navy hover:bg-cream"
                >
                  <Plus size={14} strokeWidth={ICON_STROKE} aria-hidden />
                  Add a workspace
                </button>
              )}
              {/* The founder door has to live HERE, not only on /select-workspace (#430).
                  A user with exactly one usable workspace is auto-resolved straight into
                  it by `resolveActive` and never visits the picker, and nothing else in
                  the switcher routes there (the /select-workspace navigate below is on
                  SUSPENDED rows only). So an invited member holding an unconsumed grant
                  could not reach their own create-organization button at all — the exact
                  stranding #430 set out to fix, just one screen further along. */}
              {canBootstrapTenant && (
                <button
                  role="menuitem"
                  onClick={() => { setOpen(false); setShowNew('bootstrap'); }}
                  className="flex w-full items-center gap-2 px-3 py-2 bg-transparent border-none cursor-pointer text-left font-sans text-[11px] font-semibold uppercase tracking-[0.06em] text-slate transition-colors duration-150 hover:text-navy hover:bg-cream"
                >
                  <Plus size={14} strokeWidth={ICON_STROKE} aria-hidden />
                  Create organization
                </button>
              )}
            </>
          )}
        </div>
      )}

      {showNew === 'workspace' && <NewWorkspaceDialog onClose={() => setShowNew(null)} />}
      {showNew === 'bootstrap' && <FounderBootstrapDialog onClose={() => setShowNew(null)} />}
    </div>
  );
}
